import { formatDurationString } from '@gonfalon/datetime/formatDurationString';
import { getTreatmentVariationIndex } from '@gonfalon/experiments/getTreatmentVariationIndex';
import { hasWarehouseAssignment } from '@gonfalon/experiments/hasWarehouseAssignment';
import type { Experiment, Iteration, Treatment } from '@gonfalon/experiments/types';
import { getVariationFillColor } from '@gonfalon/flag-variations/getVariationFillColor';
import type { FeatureFlag } from '@gonfalon/flags/types';
import { DateFormat } from '@gonfalon/format/constants';
import { format } from '@gonfalon/format/format';
import type { Flag } from '@gonfalon/instructions/instructionTypes';
import { msg } from '@gonfalon/messages/msg';

import type { StopMenuItemData } from './types';

export const getTargetingRule = ({
  flag,
  experiment,
  iteration,
}: {
  flag?: Flag;
  experiment?: Experiment;
  iteration?: Iteration;
}) => {
  if (!iteration || !experiment || !flag?.environments?.[experiment.environmentKey]?.rules) {
    return undefined;
  }
  const flagsEntries = Object.entries(iteration.flags || {});
  if (flagsEntries.length === 0) {
    return undefined;
  }
  const firstFlagConfig = flagsEntries[0][1];
  const environmentRules = flag.environments[experiment.environmentKey]?.rules;
  return environmentRules?.find((rule) => rule._id === firstFlagConfig?.targetingRule);
};

export const createStopItem =
  (flag: Flag | undefined, winningTreatmentId?: string) =>
  (treatment: Treatment, index: number): StopMenuItemData => {
    const color = getVariationFillColor(
      getTreatmentVariationIndex({ treatment, variations: flag?.variations, fallbackIndex: index }),
    );
    const isControl = treatment.baseline ?? false;
    const isWinning = winningTreatmentId === treatment._id;

    return {
      id: `treatment-${treatment._id ?? index}`,
      name:
        treatment.name ??
        (isControl
          ? msg('experiments.stopMenu.controlTreatment')
          : msg('experiments.stopMenu.treatmentLabel', { number: index + 1 })),
      color,
      isControl,
      isWinning,
      treatmentId: treatment._id ?? '',
    };
  };

export const getDisabledReason = (flag?: FeatureFlag, iteration?: Iteration, experiment?: Experiment) => {
  if (flag?.archived) {
    return msg('experiments.stopMenu.disabledFlagArchived');
  }
  if (!flag && !hasWarehouseAssignment(experiment, iteration)) {
    return msg('experiments.stopMenu.disabledNoFlag');
  }
  if (!iteration) {
    return msg('experiments.stopMenu.disabledNoIteration');
  }
  if (!experiment) {
    return msg('experiments.stopMenu.disabledNoExperiment');
  }
};

export function calculateDuration(startedAt?: number, endedAt?: number, short?: boolean): string {
  if (!startedAt) {
    return '';
  }

  if (short && endedAt) {
    return formatDurationString(endedAt, startedAt);
  }

  const start = new Date(startedAt);
  const end = endedAt ? new Date(endedAt) : new Date();
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const startFormatted = format(start, DateFormat.MMM_D_YYYY) ?? '';

  if (endedAt) {
    const endFormatted = format(end, DateFormat.MMM_D_YYYY) ?? '';
    return msg('experiments.stopMenu.durationRange', {
      days: diffDays,
      start: startFormatted,
      end: endFormatted,
    });
  }

  return msg('experiments.stopMenu.durationSince', { days: diffDays, start: startFormatted });
}
